import { DateTime, Duration } from 'luxon'

import type { AssetTrackingData } from '@/feature/telemetry'

import { Asset } from '@/lib/features/Assets/interfaces'

/**
 * Fallback offline timeout, in minutes, for organizations that have not
 * configured `vehicleOfflineAfter`. Matches the default the map renders with.
 */
export const DEFAULT_OFFLINE_AFTER_MINUTES = 24 * 60

/** Tooltip on a dropdown option that offline status has made unselectable. */
export const OFFLINE_BLOCKED_TOOLTIP = 'Asset currently offline'

/** Row-level error when the asset picked for a share is offline. */
export const OFFLINE_BLOCKED_ERROR = 'Vehicle is currently offline and cannot be shared'

/**
 * Shown where an offline asset is shareable anyway, because its device is only
 * fetched inside its windows and so having no recent fix is expected.
 */
export const OFFLINE_EASED_NOTICE = 'Not currently reporting a location — sharing is still allowed'

/**
 * The organization's offline timeout in minutes, falling back to the default.
 *
 * Organizations can configure `vehicleOfflineAfter` (the same setting that
 * drives lost-contact colouring on the map), so the share flow must honour it
 * rather than assuming 24 hours.
 */
export const resolveOfflineAfterMinutes = (vehicleOfflineAfter?: number | null) =>
  vehicleOfflineAfter ?? DEFAULT_OFFLINE_AFTER_MINUTES

/**
 * True when telemetry carries a provider fix inside the offline timeout.
 */
export const hasRecentLocationFix = (
  tracking: AssetTrackingData | undefined,
  offlineAfterMinutes: number,
) =>
  tracking?.sourceDatetime != null &&
  DateTime.fromJSDate(tracking.sourceDatetime).diffNow() >=
    Duration.fromObject({ minutes: -offlineAfterMinutes })

/**
 * True when an asset has no usably recent location, whatever the reason.
 */
export const isOffline = (tracking: AssetTrackingData | undefined, offlineAfterMinutes: number) =>
  !hasRecentLocationFix(tracking, offlineAfterMinutes)

/**
 * True when offline status must stop the asset being shared.
 *
 * Window-gated assets (`intermittentLocation` — a subbie-owned device on a
 * conditional-pull source) are exempt: Intake only fetches them inside their
 * fetch windows, so no recent fix is expected rather than a fault. Every other
 * asset is still blocked while offline. See BRI-1503.
 */
export const isShareBlockedByOffline = (
  asset: Pick<Asset, 'intermittentLocation'> | null | undefined,
  tracking: AssetTrackingData | undefined,
  offlineAfterMinutes: number,
) => isOffline(tracking, offlineAfterMinutes) && !asset?.intermittentLocation

/**
 * True when an asset is offline but exempt from the block — the case worth
 * telling the operator about, since they can share it despite the gap.
 */
export const shouldNoticeEasedOffline = (
  asset: Pick<Asset, 'intermittentLocation'> | null | undefined,
  tracking: AssetTrackingData | undefined,
  offlineAfterMinutes: number,
) => isOffline(tracking, offlineAfterMinutes) && !!asset?.intermittentLocation
